// Classifies the user's reply into a session mode (tradizionale/mappatura/
// scenari_futuri) while sessionMode is still null, and detects an explicit
// mid-session mode-switch request once it's set. Mirrors intentRouter.js's
// and moveRouter.js's pure-rule-first, LLM-fallback pattern.
import { userTurns } from "./chatHistory";
import { renderStr } from "./prompts";
import { wordRe, perLang } from "./langRegex";

export const MODES = ["tradizionale", "mappatura", "scenari_futuri"];

// Keyword regexes per language, built from vocab(lang).mode_keywords (regex
// bodies, shared with backend/pocketflow_agents.py's pick_mode).
const modeKeywords = (lang) => perLang("mode_keywords", lang, (v) =>
  Object.fromEntries(Object.entries(v.mode_keywords).map(([m, body]) => [m, wordRe(body)])));

// Pure: derives a mode from keyword matches in the user's last message, or
// null if no clear keyword match OR more than one mode's keywords match
// (e.g. "mappare gli scenari futuri" hits both mappatura's "mappare" and
// scenari_futuri's "scenari" — picking whichever mode happened to be first
// in MODES would be an arbitrary, silently-wrong guess for genuinely
// ambiguous phrasing). Ambiguous cases fall through to classifyMode's LLM
// call below, which reads the whole phrase's semantics instead of matching
// isolated keywords.
export function pickMode({ chat, lang = "it" }) {
  const turns = userTurns(chat);
  const lastUser = turns[turns.length - 1] || "";
  const keywords = modeKeywords(lang);
  const matches = MODES.filter((mode) => keywords[mode].test(lastUser));
  return matches.length === 1 ? matches[0] : null;
}

// Finds which candidate appears earliest in `raw` — same position-based
// matching as intentRouter.js/moveRouter.js's firstMatchByPosition.
function firstMatchByPosition(raw, candidates) {
  let best = null;
  let bestIndex = Infinity;
  for (const c of candidates) {
    const m = new RegExp(`\\b${c}\\b`, "i").exec(raw || "");
    if (m && m.index < bestIndex) {
      best = c;
      bestIndex = m.index;
    }
  }
  return best;
}

// Ambiguous case: one short LLM call returning a single word. Returns null
// (not a default) on no match or failure — the caller must treat that as
// "still unclear" so the ask/redirect/default-after-2 state machine can
// increment its attempt counter, unlike classifyIntent/classifyMove which
// safely default because misrouting there is much lower-stakes.
export async function classifyMode({ chat, generate, lang = "it" }) {
  try {
    const raw = await generate(renderStr("mode_classifier", lang, { chat, modes: MODES.join("|") }));
    return firstMatchByPosition(raw, MODES);
  } catch (e) {
    return null;
  }
}
